/**
 * A decoded Sparkplug payload, for the JSONPath builder to browse.
 *
 * ── Why this is a constant and not a request ──────────────────────────────
 * `value_path` and `timestamp_path` are queries into the payload as it exists
 * AFTER the wire format has been decoded server-side — a Sparkplug Protobuf message
 * becomes a plain dict before any JSONPath runs against it. So a builder needs that
 * decoded shape to click through, and there is no endpoint that serves one: the
 * MQTT worker decodes a message, logs it at debug level and extracts from it, and
 * nothing exposes a sample to the API.
 *
 * `SENSOR_EXTRACTION.md` sanctions exactly this stand-in: "until it does, the builder
 * can be seeded from a hardcoded example matching this shape."
 *
 * ── Which is why the builder also takes a pasted payload ──────────────────
 * This one is a shape, not a fact about anybody's broker. A real deployment's
 * decoded message will carry different metric names, so a path built against this
 * example resolves to nothing until the names are changed. The builder offers this
 * as the starting point and accepts a pasted payload as an equal source — see
 * JsonPathBuilder. When a sample-payload endpoint lands it becomes a third source
 * and the builder itself does not change.
 *
 * ── The shape is the real one, and that is load-bearing ───────────────────
 * This is `to_jsonable(psp.NData.decode(...))` verbatim: `dataclasses.asdict` over
 * pysparkplug's own `NData`, which is what `MqttSparkplugWorker._handle_message`
 * hands to `extract_and_publish`.
 *
 * An earlier version of this constant was hand-written and WRONG in two ways that
 * both produce a sensor which saves, validates and then reads nothing:
 *
 *   - it carried a top-level `topic`. The decoded payload has no such key — the
 *     topic is the MQTT envelope, not the message, and the worker matches on it
 *     before any JSONPath runs. `$.topic` is therefore a path the builder offered
 *     and the pipeline can never resolve.
 *   - each metric carried a `properties: {unit, quality}` object. Sparkplug allows
 *     one; pysparkplug's `Metric` dataclass has no such field, so it is absent from
 *     every decoded message this backend produces.
 *
 * Both validated cleanly against `^\$.*` and failed silently at ingest, which is
 * the worst available failure for this screen. The fields below are the complete
 * set — nothing is trimmed for tidiness, because a key missing from the browser is
 * a path somebody cannot build.
 *
 * `metrics` is an array because one Sparkplug message carries several metrics
 * together — Temperature and Pressure in the same NDATA — which is the entire reason
 * a sensor needs a path rather than just a topic. Selecting one by name
 * (`$.metrics[?name="Temperature"].value`) rather than by index is what survives the
 * broker reordering them; `jsonpath_ng.ext` is the server-side parser and it is
 * what supports that filter syntax.
 *
 * `datatype` is Sparkplug's own type code, not this product's `value_type` — 10 is
 * DOUBLE. `alias` is the Edge Node's stable short id for the metric, assigned at
 * birth. Neither is something a value path normally reads; both are here because
 * they are in the message.
 *
 * Not translated, not localised, `translate="no"` wherever it renders: this is a
 * machine payload.
 */
export const SAMPLE_PAYLOAD = {
  timestamp: 1786091250330,
  seq: 3,
  metrics: [
    {
      timestamp: 1786091250330,
      name: 'Temperature',
      datatype: 10,
      value: 42.5,
      alias: 0,
      is_historical: false,
      is_transient: false,
      is_null: false,
    },
    {
      timestamp: 1786091250330,
      name: 'Pressure',
      datatype: 10,
      value: 101.3,
      alias: 1,
      is_historical: false,
      is_transient: false,
      is_null: false,
    },
  ],
}

/** Pretty-printed, for the paste box's initial content and the "reset" affordance. */
export const SAMPLE_PAYLOAD_TEXT = JSON.stringify(SAMPLE_PAYLOAD, null, 2)
